<template>
  <div class="space-y-4">
    <div class="rounded-md bg-amber-50 border border-amber-200 p-3">
      <p class="text-sm text-amber-800">
        <strong>Note:</strong> This preset is intended for devices that assist, enable, or perform a
        medical procedure, regardless of the underlying condition. If your review focuses on a
        specific medical condition, we recommend using the "Medical Background of a condition"
        preset instead.
      </p>
    </div>

    <div class="space-y-4">
      <div>
        <p class="text-black text-sm font-medium">Procedure name</p>
        <p class="text-xs pointer-events-none">
          Enter the name of the procedure for which you would like to create the review.
        </p>
      </div>
      <div v-if="preset.procedureName" class="flex flex-wrap gap-2 mt-2">
        <span
          class="bg-tertiary-50 text-tertiary-500 px-4 py-2 rounded-lg flex items-center text-sm font-medium"
        >
          {{ preset.procedureName }}
          <button class="ml-2 text-tertiary-500" @click="reset">✕</button>
        </span>
      </div>
      <TextInput
        v-if="!preset.procedureName"
        class="w-96"
        @enter="(procedureName) => acceptProcedureName(procedureName)"
      >
        <template #suffix="{ value }">
          <button
            class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-primary-500 px-3 py-2 text-xs text-white shadow-md transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
            @click="acceptProcedureName(value)"
          >
            Enter
            <ArrowTurnDownLeftIcon class="w-4 h-4 text-white" />
          </button>
        </template>
      </TextInput>
    </div>

    <div v-if="isFindingAlternativeProcedureNames">
      <Loader message="Finding alternative procedure names..." />
    </div>

    <div v-if="preset.procedureName && !isFindingAlternativeProcedureNames">
      <div class="flex items-center justify-between">
        <div>
          <span class="text-sm text-black font-medium pointer-events-none">
            Alternative procedure names
          </span>
          <p class="text-xs pointer-events-none">
            Which other names are commonly used for the procedure?
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2 mt-2">
        <span
          v-for="name in preset.alternativeProcedureNames"
          :key="name"
          class="px-3 py-1 rounded-lg flex items-center text-sm font-medium bg-tertiary-50 text-tertiary-500"
        >
          {{ name }}
          <button class="ml-2 text-red-500" @click="removeAlternativeName(name)">✕</button>
        </span>

        <TextInput
          v-model="newAlternativeName"
          class="w-96"
          placeholder="Enter additional names"
          @enter="addAlternativeName"
        >
          <template #suffix>
            <button
              class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-primary-500 px-3 py-2 text-xs text-white shadow-md transition hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
              @click="addAlternativeName"
            >
              Enter
              <ArrowTurnDownLeftIcon class="w-4 h-4 text-white" />
            </button>
          </template>
        </TextInput>
      </div>
      <button
        v-if="!hasAcceptedAlternativeNames"
        class="px-2 py-1 bg-primary-500 text-white rounded-md mt-4"
        @click="acceptAlternativeProcedureNames"
      >
        Accept
      </button>
    </div>

    <div v-if="preset.procedureName && hasAcceptedAlternativeNames">
      <p class="text-sm text-black font-medium pointer-events-none">Purpose</p>
      <p class="text-xs pointer-events-none">
        Specify the medical purpose that you want to analyze for the procedure.
      </p>
      <div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          <template v-for="purpose in purposes" :key="purpose">
            <label
              :for="`objective-${purpose}`"
              class="font-medium text-gray-900 select-none p-3 flex items-center justify-between border rounded-lg"
            >
              {{ purpose }}
              <div class="group grid size-4 grid-cols-1">
                <input
                  :id="`objective-${purpose}`"
                  v-model="preset.purpose"
                  :name="`objective-${purpose}`"
                  type="radio"
                  :value="purpose"
                  class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-primary-600 checked:bg-primary-600 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                />
              </div>
            </label>
          </template>
        </div>
        <button
          v-if="!hasAcceptedPurpose"
          class="px-2 py-1 rounded-md mt-4"
          :class="{
            'bg-gray-300 text-gray-100 cursor-not-allowed': !preset.purpose,
            'bg-primary-500 text-white': preset.purpose,
          }"
          :disabled="!preset.purpose"
          @click="acceptPurpose"
        >
          Accept
        </button>
      </div>
    </div>

    <div v-if="isDeterminingLOE">
      <Loader message="Determining level of evidence..." />
    </div>
    <div
      v-if="
        preset.procedureName &&
        hasAcceptedPurpose &&
        hasAcceptedAlternativeNames &&
        !isDeterminingLOE
      "
    >
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-black font-medium pointer-events-none">Target level of evidence</p>
          <p class="text-xs pointer-events-none">
            To investigate the procedure {{ preset.procedureName }}, we recommend considering the
            following level of evidence. Do you agree?
          </p>
        </div>
      </div>

      <div class="mt-2">
        <Pyramid
          v-model="preset.targetLoe"
          :segments="[
            {
              level: 50,
              label: 'Evidence-based clinical guidelines',
              color: '#06b6d4',
              id: 'a',
            },
            {
              level: 60,
              label: 'Systematic reviews and meta-analyses',
              color: '#84cc16',
              id: 'b',
            },
            {
              level: 70,
              label: 'Randomized controlled trials',
              color: '#eab308',
              id: 'c',
            },
            {
              level: 80,
              label: 'Observational studies and cohort studies',
              color: '#fb923c',
              id: 'd',
            },
            {
              level: 90,
              label: 'Case studies, case series',
              color: '#f472b6',
              id: 'e',
            },
            {
              level: 100,
              label: 'Narrative sources (reviews, textbooks, expert opinion)',
              color: '#e879f9',
              id: 'f',
            },
          ]"
        />
      </div>
      <button
        v-if="!hasAcceptedTargetLOE"
        class="px-2 py-1 rounded-md mt-4"
        :class="{
          'bg-gray-300 text-gray-100 cursor-not-allowed': !preset.targetLoe,
          'bg-primary-500 text-white': preset.targetLoe,
        }"
        :disabled="!preset.targetLoe"
        @click="acceptTargetLOE"
      >
        Accept
      </button>
    </div>

    <div v-if="isDeterminingTimeFrame">
      <Loader message="Determining time frame..." />
    </div>
    <div
      v-if="
        preset.procedureName &&
        hasAcceptedPurpose &&
        hasAcceptedAlternativeNames &&
        hasAcceptedTargetLOE &&
        !isDeterminingTimeFrame
      "
    >
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm text-black font-medium pointer-events-none">Time range</p>
          <p class="text-xs pointer-events-none">
            To investigate the procedure {{ preset.procedureName }}, we recommend considering the
            following time range. Do you agree?
          </p>
        </div>
      </div>
      <div class="space-y-6 sm:flex sm:items-center sm:space-y-0 sm:space-x-10 mt-2">
        <div
          v-for="range in [
            { value: 1, label: '1 year' },
            { value: 5, label: '5 years' },
            { value: 10, label: '10 years' },
          ]"
          :key="range.value"
          class="flex items-center"
        >
          <input
            :id="range.value.toString()"
            v-model="preset.timeRange"
            :value="range.value"
            name="time-range"
            type="radio"
            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-primary-600 checked:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
          />
          <label
            :for="range.value.toString()"
            class="ml-3 block text-sm/6 font-medium text-gray-900"
          >
            {{ range.label }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { ArrowTurnDownLeftIcon } from "@heroicons/vue/24/outline";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import Pyramid from "@app/components/Global/Pyramid.vue";
import { useProcedurePresetAssistant } from "./assistant";
import { injectStrict } from "@app/utils/injectStrict";
import { CreateReviewKey } from "../../../useCreateProjectReview";
import { generateTimeLimitRange } from "@app/utils/timeLimits";
import Loader from "@app/components/Loader.vue";

const purposes = [
  "Prevention",
  "Prediction",
  "Prognosis",
  "Diagnosis",
  "Treatment",
  "Alleviation",
  "Monitoring",
  "Compensation",
];

const { findProcedureNames, determineTimeFrame, generateLevelOfEvidence } =
  useProcedurePresetAssistant();

const { preset } = injectStrict(CreateReviewKey);

const hasAcceptedPurpose = ref(false);
const hasAcceptedAlternativeNames = ref(false);
const hasAcceptedTargetLOE = ref(false);

const isFindingAlternativeProcedureNames = ref(false);
const isDeterminingLOE = ref(false);
const isDeterminingTimeFrame = ref(false);

async function acceptProcedureName(procedureName: string) {
  preset.value.procedureName = procedureName;
  isFindingAlternativeProcedureNames.value = true;
  preset.value.alternativeProcedureNames = await findProcedureNames({
    procedureName,
  }).catch(() => []);
  isFindingAlternativeProcedureNames.value = false;
}

async function acceptPurpose() {
  hasAcceptedPurpose.value = true;
  isDeterminingLOE.value = true;
  preset.value.targetLoe = await generateLevelOfEvidence({
    procedureNames: [preset.value.procedureName, ...preset.value.alternativeProcedureNames].join(
      ", ",
    ),
    purpose: preset.value.purpose,
  }).catch(() => "");
  isDeterminingLOE.value = false;
}

async function acceptAlternativeProcedureNames() {
  hasAcceptedAlternativeNames.value = true;
}

async function acceptTargetLOE() {
  hasAcceptedTargetLOE.value = true;
  isDeterminingTimeFrame.value = true;
  preset.value.timeRange = await determineTimeFrame({
    procedureNames: [preset.value.procedureName, ...preset.value.alternativeProcedureNames].join(
      ", ",
    ),
    purpose: preset.value.purpose,
  }).catch(() => undefined);
  isDeterminingTimeFrame.value = false;
}

function reset() {
  preset.value.procedureName = "";
  preset.value.purpose = "";
  preset.value.alternativeProcedureNames = [];
  preset.value.targetLoe = "";
  preset.value.timeRange = undefined;
  preset.value.timeLimitFromDate = undefined;
  preset.value.timeLimitToDate = undefined;
  hasAcceptedAlternativeNames.value = false;
  hasAcceptedPurpose.value = false;
  hasAcceptedTargetLOE.value = false;
  isFindingAlternativeProcedureNames.value = false;
  isDeterminingLOE.value = false;
  isDeterminingTimeFrame.value = false;
}

const newAlternativeName = ref("");

function addAlternativeName() {
  const name = newAlternativeName.value.trim();
  if (!name || preset.value.alternativeProcedureNames.includes(name)) return;
  preset.value.alternativeProcedureNames.push(name);
  newAlternativeName.value = "";
}

function removeAlternativeName(name: string) {
  preset.value.alternativeProcedureNames = preset.value.alternativeProcedureNames.filter(
    (n) => n !== name,
  );
}

watch(
  () => preset.value.timeRange,
  (newValue) => {
    if (newValue) {
      const range = generateTimeLimitRange(newValue);
      if (!range) return;
      preset.value.timeLimitFromDate = range[0];
      preset.value.timeLimitToDate = range[1];
    }
  },
);
</script>
